import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileUp, File, Loader2 } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from '../components/hooks/use-toast';
import { api } from '../services/api';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store/store';
import { updateCurrentUser } from 'wasp/client/operations';
import { setIsGetStartedDialogOpen, setReimportExcelDialogOpen } from '../store/flowSlice';
import { deleteGenericJob, startGenericJob } from '../store/jobsSlice';
import { GenericJobService } from '../services/job/GenericJobService';
import { useTranslation, TranslationKey } from '../i18n/hooks';

const ExcelReimportView = () => {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const uploadedFile = acceptedFiles[0];
    if (uploadedFile?.type.toLocaleLowerCase() !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' &&
        uploadedFile?.type.toLocaleLowerCase() !== 'application/vnd.ms-excel' &&
        uploadedFile?.type.toLocaleLowerCase() !== 'application/vnd.ms-excel.sheet.macroenabled.12') {
      toast({
        variant: "destructive",
        title: t("excel_import.invalid_file_type" as any as TranslationKey),
        description: t("excel_import.upload_excel_file" as any as TranslationKey),
      });
      return;
    }
    setFile(uploadedFile);
  }, [t]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.ms-excel.sheet.macroenabled.12': ['.xlsm']
    },
    maxFiles: 1,
  });

  const {currentUser, currentCollection} = useSelector((state: RootState) => state.user);
  const dispatch = useDispatch();
  const handleUpload = async () => {
    if (!file) return;
    
    setIsUploading(true);
    try {
      if(!currentUser?.email){
        throw new Error("Cannot find current user's email. This is probably a bug in the application.");
      }
      if(!currentCollection?.collection){
        throw new Error("Cannot find active collection. This is probably a bug in the application.");
      }
      const jobRes = await api.postExcelToInternalTable({
        account_id: currentUser?.email,
        collection_name: currentCollection?.collection?.name,
        file: file,
      });
      console.log(jobRes)
      if(!jobRes?.data?.job_id){
        throw new Error("Failed to upload file. " + jobRes?.data?.message);
      }

      dispatch(setReimportExcelDialogOpen(false));
      dispatch(startGenericJob({
        identifier: "reimport_excel",
        trackingId: jobRes.data.job_id,
      }));
      GenericJobService.getInstance().ensurePolling();
      toast({
        title: t("excel_import.success" as any as TranslationKey),
        description: t("excel_import.file_uploaded_successfully" as any as TranslationKey),
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: t("excel_import.error" as any as TranslationKey),
        description: t("excel_import.failed_to_upload" as any as TranslationKey),
      });
      dispatch(deleteGenericJob("reimport_excel"));
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="container mx-auto mt-6">
     
      <Card className="p-6 bg-gray-50 dark:bg-gray-900 p-4 rounded-lg border-0">
        <div
          {...getRootProps()}
          className={`
            border-2 border-dashed rounded-lg p-8
            flex flex-col items-center justify-center
            cursor-pointer transition-colors
            ${isDragActive ? 'border-primary bg-primary/5' : 'border-gray-300'}
            hover:border-primary hover:bg-primary/5
          `}
        >
          <input {...getInputProps()} />
          <Upload className="h-12 w-12 text-gray-400 mb-4" />
          {isDragActive ? (
            <p className="text-center text-lg">{t("excel_import.drop_excel_here" as any as TranslationKey)}</p>
          ) : (
            <div className="text-center">
              <p className="text-lg mb-2">{t("excel_import.drag_drop_excel" as any as TranslationKey)}</p>
              <p className="text-sm text-gray-500">{t("excel_import.or_click_to_select" as any as TranslationKey)}</p>
            </div>
          )}
        </div>

        {file && (
          <div className="mt-4 p-4 bg-secondary rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              <File className="h-5 w-5" />
              <span className="font-medium">{file.name}</span>
            </div>
            <Button
              onClick={handleUpload}
              disabled={isUploading}
            >
              {isUploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("excel_import.uploading" as any as TranslationKey)}
                </>
              ) : (
                <>
                  <FileUp className="mr-2 h-4 w-4" />
                  {t("excel_import.upload" as any as TranslationKey)}
                </>
              )}
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ExcelReimportView;